import type { ExecutionIncident } from '../incidentFacade';
import {
  getIncidentTypeLabel,
  isBlockingIncidentType
} from '../incidentFacade';
import type { IncidentProjection, IncidentsProjection, IncidentSeverity } from './types';

const ETA_AFFECTING = new Set(['downtime', 'breakdown', 'accident', 'date_reschedule', 'redirection']);
const FINANCIAL_AFFECTING = new Set([
  'downtime',
  'accident',
  'partial_unload',
  'overload',
  'unload_refusal'
]);

function incidentSeverity(incident: ExecutionIncident): IncidentSeverity {
  if (incident.status === 'resolved') return 'low';
  if (incident.blockingFlag || isBlockingIncidentType(incident.incidentType)) return 'high';
  if (incident.incidentType === 'downtime' || incident.incidentType === 'breakdown') return 'medium';
  return 'low';
}

export function buildIncidentProjection(incident: ExecutionIncident): IncidentProjection {
  return {
    id: incident.id,
    type: incident.incidentType,
    typeLabel: getIncidentTypeLabel(incident.incidentType),
    severity: incidentSeverity(incident),
    status: incident.status,
    isActive: incident.status === 'open',
    startedAt: incident.occurredAt,
    resolvedAt: incident.resolvedAt,
    comment: incident.comment,
    affectsETA: ETA_AFFECTING.has(incident.incidentType),
    financialImpact: FINANCIAL_AFFECTING.has(incident.incidentType),
    relatedExecutionStopUid: incident.relatedExecutionStopUid
  };
}

export function buildIncidentsProjection(incidents: ExecutionIncident[]): IncidentsProjection {
  const items = incidents.map(buildIncidentProjection);
  const active = items.filter((i) => i.isActive);
  return {
    items,
    hasActiveBlocking: active.some(
      (i) => i.severity === 'high' || incidents.find((raw) => raw.id === i.id)?.blockingFlag
    ),
    activeCount: active.length
  };
}
